import axios from "axios";

// Resolve the backend base URL safely so the app works on:
//   - local dev (CRA proxy / preview URL)
//   - default deployed domain (*.emergent.host)
//   - custom domains connected after deployment
//
// The build-time REACT_APP_BACKEND_URL is baked into the bundle, so if the
// user serves the same bundle from a custom domain it would otherwise point
// at the old preview URL → cross-origin + cookie domain mismatch → login
// breaks. We fall back to `window.location.origin` whenever the configured
// backend origin doesn't match the page we're actually on. The /api/* path
// is always proxied through whatever domain serves the frontend.
function resolveBackendUrl() {
    const envUrl = process.env.REACT_APP_BACKEND_URL || "";
    if (typeof window === "undefined") return envUrl;
    const pageOrigin = window.location.origin;
    if (!envUrl) return pageOrigin;
    try {
        const envOrigin = new URL(envUrl).origin;
        return envOrigin === pageOrigin ? envUrl : pageOrigin;
    } catch {
        return pageOrigin;
    }
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "proxiq.session";

const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// Centralised auth handling:
// - On 401, clear the local session flag and bounce the user to /login.
// - We return a *pending* promise so the caller's .then / .catch never fires,
//   which prevents axios rejections from surfacing as the dev "Uncaught
//   runtime errors" overlay during the redirect.
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        if (status === 401) {
            try {
                localStorage.removeItem(SESSION_KEY);
            } catch {}
            // Don't redirect for the /auth/me probe — AuthContext handles that
            // path itself (it expects 401 when not logged in).
            const onLogin = window.location.pathname === "/login";
            const isMeProbe = url.includes("/auth/me");
            if (!onLogin && !isMeProbe) {
                window.location.replace("/login");
                return new Promise(() => {}); // never settles → no caller error
            }
        }
        return Promise.reject(error);
    },
);

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) =>
                e && typeof e.msg === "string" ? e.msg : JSON.stringify(e),
            )
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
